import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import type { Combo, ComboPizzaFlavor, ComboItem, ComboItemOption } from "./useCombos";

export type InternalComboChannel = 'pdv' | 'table' | 'tablet' | 'delivery';

// Slot-based types (modern model)
export interface ComboSlotProduct {
  id: string;
  name: string;
  price: number;
  image_url?: string | null;
}

export interface ComboSlotWithProducts {
  id: string;
  slot_type: 'category' | 'product';
  category_id: string | null;
  product_id: string | null;
  min_quantity: number;
  max_quantity: number;
  pricing_rule: 'highest' | 'average' | 'fixed' | 'rodizio';
  fixed_price: number | null;
  label: string | null;
  is_required: boolean;
  display_order: number;
  products?: ComboSlotProduct[];
  product?: ComboSlotProduct;
  category?: { name: string };
}

export interface InternalComboWithDetails extends Combo {
  // Legacy model (for backwards compatibility)
  pizzaFlavors?: ComboPizzaFlavor[];
  items?: (ComboItem & { options?: ComboItemOption[] })[];
  // Modern model (combo_slots)
  slots?: ComboSlotWithProducts[];
  // Flag to indicate which model to use for rendering
  useSlotModel?: boolean;
}

/**
 * Hook for fetching combos with their details for internal PDV/Waiter use.
 * Supports both legacy (combo_items/combo_pizza_flavors) and modern (combo_slots) models.
 * Falls back to combo_slots when legacy data is empty.
 */
export function useInternalCombos(channel: InternalComboChannel = 'pdv') {
  const { activeRestaurant } = useRestaurantContext();
  const restaurantId = activeRestaurant?.id;

  return useQuery({
    queryKey: ['internal-combos', restaurantId, channel],
    queryFn: async () => {
      if (!restaurantId) return [];

      // Build the channel filter
      const channelField = `available_${channel}` as const;

      const { data: combos, error } = await supabase
        .from('combos')
        .select(`
          *,
          category:categories!combos_category_id_fkey(name),
          rodizio_category:categories!combos_rodizio_category_id_fkey(name)
        `)
        .eq('restaurant_id', restaurantId)
        .eq('is_active', true)
        .eq(channelField, true)
        .order('name');

      if (error) throw error;
      if (!combos || combos.length === 0) return [];

      // Fetch details for each combo
      const combosWithDetails: InternalComboWithDetails[] = [];

      for (const combo of combos) {
        let pizzaFlavors: ComboPizzaFlavor[] = [];
        let items: (ComboItem & { options?: ComboItemOption[] })[] = [];
        let slots: ComboSlotWithProducts[] = [];
        let useSlotModel = false;

        // Try legacy model first
        if (combo.combo_type === 'pizza') {
          const { data } = await supabase
            .from('combo_pizza_flavors')
            .select(`
              *,
              product:products(id, name, price, image_url)
            `)
            .eq('combo_id', combo.id)
            .order('display_order');

          pizzaFlavors = (data || []) as ComboPizzaFlavor[];
        }

        if (combo.combo_type === 'normal') {
          const { data: itemsData } = await supabase
            .from('combo_items')
            .select(`
              *,
              product:products(id, name, price)
            `)
            .eq('combo_id', combo.id)
            .order('display_order');

          if (itemsData) {
            for (const item of itemsData) {
              if (item.item_type === 'choice') {
                const { data: optionsData } = await supabase
                  .from('combo_item_options')
                  .select(`
                    *,
                    product:products(id, name, price)
                  `)
                  .eq('combo_item_id', item.id)
                  .order('display_order');

                (item as any).options = optionsData || [];
              }
            }
            items = itemsData as (ComboItem & { options?: ComboItemOption[] })[];
          }
        }

        // If legacy model is empty, try modern combo_slots model
        const legacyIsEmpty = 
          (combo.combo_type === 'pizza' && pizzaFlavors.length === 0) ||
          (combo.combo_type === 'normal' && items.length === 0) ||
          (combo.combo_type !== 'pizza' && combo.combo_type !== 'normal' && combo.combo_type !== 'rodizio');

        if (legacyIsEmpty) {
          const { data: slotsData } = await supabase
            .from('combo_slots')
            .select(`
              *,
              category:categories(id, name),
              product:products(id, name, price, image_url)
            `)
            .eq('combo_id', combo.id)
            .order('display_order');

          if (slotsData && slotsData.length > 0) {
            useSlotModel = true;

            for (const slot of slotsData) {
              const slotWithProducts: ComboSlotWithProducts = {
                id: slot.id,
                slot_type: slot.slot_type as 'category' | 'product',
                category_id: slot.category_id,
                product_id: slot.product_id,
                min_quantity: slot.min_quantity,
                max_quantity: slot.max_quantity,
                pricing_rule: slot.pricing_rule as 'highest' | 'average' | 'fixed' | 'rodizio',
                fixed_price: slot.fixed_price,
                label: slot.label,
                is_required: slot.is_required ?? true,
                display_order: slot.display_order ?? 0,
                category: slot.category ? { name: slot.category.name } : undefined,
                product: slot.product ? {
                  id: slot.product.id,
                  name: slot.product.name,
                  price: Number(slot.product.price),
                  image_url: slot.product.image_url,
                } : undefined,
              };

              // Fetch products for category-based slots
              if (slot.slot_type === 'category' && slot.category_id) {
                const { data: productsData } = await supabase
                  .from('products')
                  .select('id, name, price, image_url')
                  .eq('category_id', slot.category_id)
                  .eq('is_active', true)
                  .order('name');

                slotWithProducts.products = (productsData || []).map(p => ({
                  id: p.id,
                  name: p.name,
                  price: Number(p.price),
                  image_url: p.image_url,
                }));
              }

              slots.push(slotWithProducts);
            }
          }
        }

        combosWithDetails.push({
          ...(combo as Combo),
          pizzaFlavors,
          items,
          slots,
          useSlotModel,
        });
      }

      return combosWithDetails;
    },
    enabled: !!restaurantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
